import { formatDate } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  DoCheck,
  EventEmitter,
  forwardRef,
  HostListener,
  Inject,
  Injector,
  Input,
  LOCALE_ID,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { DateObjectUnits, DateTime } from 'luxon';
import { PrimeTemplate } from 'primeng/api';
import { ValidatorsPlus } from '../../../common/validators/plus.validator';
import { FORM_VALIDATION_ERROR_MSG_MAP } from '../../../core/constants/message.constant';
import { isNullOrEmpty } from '../../../core/utils/common-functions';
import { getFormGroupEx } from '../../../core/utils/validator-functions';
import { DateRangeSetting } from '../../../models/rt-common';
import { BaseComponent } from '../base.component';
import { DatePickerComponent } from '../date-picker/date-picker.component';

/**
 * 2024/03/04 新增 DateRangeSetting可檢核起迄區間最大值
 */
export const DATE_RANGE_PICKER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DateRangePickerComponent),
  multi: true,
};

enum DateRangeTypeEnum {
  start,
  end,
}

const DAY_STRART_CONFIG: DateObjectUnits = {
  hour: 0,
  minute: 0,
  second: 0,
  millisecond: 0,
};
const DAY_END_CONFIG: DateObjectUnits = {
  hour: 0,
  minute: 0,
  second: 0,
  millisecond: 0,
};

/**
 * 日期區間元件
 *
 * @export
 * @class DateRangePickerComponent
 * @extends {BaseComponent}
 * @implements {OnInit}
 * @implements {OnChanges}
 * @implements {AfterViewInit}
 */
@Component({
  selector: 'sigv-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  providers: [DATE_RANGE_PICKER_VALUE_ACCESSOR],
})
export class DateRangePickerComponent
  extends BaseComponent
  implements
    OnInit,
    OnChanges,
    DoCheck,
    AfterViewInit,
    AfterViewChecked,
    ControlValueAccessor
{
  private _control: AbstractControl | null = null;
  private previousModelJson = JSON.stringify({});
  readonly validationMsgMap = FORM_VALIDATION_ERROR_MSG_MAP;
  private waitForUpdate = false;

  @Input() fieldName: string = '';
  @Input() isDateRangeRequired = false;
  /**
   * 只可選取
   */
  @Input() readonlyInput = false;

  /**
   * 移動元件至
   */
  @Input() appendTo: any;

  /**
   * 日期格式
   */
  @Input() dateFormat: any;

  /**
   * Placeholder
   */
  @Input() placeholder = '';

  /**
   * Disabled
   */
  @Input() disabled = false;

  /**
   * 最小值
   */
  @Input() minDate?: Date;

  /**
   * 最大值
   */
  @Input() maxDate?: Date;

  /**
   * 年份下拉區間
   */
  @Input() yearRange?: string;

  /**
   * 顯示時間
   */
  @Input() showTime = false;

  /**
   * 分鐘 Tick
   */
  @Input() stepMinute = 1;

  /**
   * 起日
   * @deprecated 因加入了格式驗證必須使用 ngModel 或 form 的方式，不能單純使用 start/end
   */
  @Input() start: any;

  /**
   * 迄日
   * @deprecated 因加入了格式驗證必須使用 ngModel 或 form 的方式，不能單純使用 start/end
   */
  @Input() end: any;

  /**
   * FormControlName
   */
  @Input() formControlName?: string;

  /**
   * 是否唯讀
   */
  @Input() readonly = false;
  /**
   *
   */
  @Input() rangeSetting?: DateRangeSetting;
  /**
   *
   */
  @Input() greaterThenSysDateCheck = false;
  /**
   *
   */
  @Input() lessThenSysDateCheck = false;
  /**
   * 是否點過送出按鈕，觸發欄位下方檢核紅字
   */
  @Input() submitTouched = false;
  /**
   * 觸發檢核
   */
  @Input() forceUpdateValueAndValidity = false;

  /**
   * 起日變更事件
   * @deprecated 因加入了格式驗證必須使用 ngModel 或 form 的方式，不能單純使用 start/end
   */
  @Output() startChange = new EventEmitter<any>();

  /**
   * 迄日變更事件
   * @deprecated 因加入了格式驗證必須使用 ngModel 或 form 的方式，不能單純使用 start/end
   */
  @Output() endChange = new EventEmitter<any>();

  /**
   * Blur 事件
   */
  @Output() dateBlur = new EventEmitter<{ target: string; originEvent: any }>();

  /**
   * Clear 事件
   */
  @Output() dateClear = new EventEmitter<{
    target: string;
    originEvent: any;
  }>();

  /**
   * 選取事件
   */
  @Output() dateSelect = new EventEmitter<{ target: string; value: Date }>();

  /**
   * Focus 事件
   */
  @Output() dateFocus = new EventEmitter<{
    target: string;
    originEvent: any;
  }>();

  /**
   * 輸入事件
   */
  @Output() dateInput = new EventEmitter<{
    target: string;
    originEvent: any;
  }>();
  /**
   * 取得元件內的control
   */
  @Output() getControl = new EventEmitter<AbstractControl>();
  @Output() backspacePressed = new EventEmitter<void>();
  @ViewChild('startCalendar') startCalendar!: DatePickerComponent;
  @ViewChild('endCalendar') endCalendar!: DatePickerComponent;
  @ContentChildren(PrimeTemplate) templates!: QueryList<PrimeTemplate>;
  startErrorMessageTemplate!: TemplateRef<any>;
  endErrorMessageTemplate!: TemplateRef<any>;
  startTemplate!: TemplateRef<any>;
  endTemplate!: TemplateRef<any>;
  model: { start?: Date; end?: Date } = {};
  translateParams?: { [key: string]: string | undefined };
  primeDateFormat: any;
  ngDateFormat = 'MM/dd/yyyy';
  onModelChange = (event: {
    start?: Date | undefined;
    end?: Date | undefined;
  }) => {};
  onModelTouched = () => {};

  constructor(
    private injector: Injector,
    @Inject(LOCALE_ID) private locale: string,
    private cd: ChangeDetectorRef
  ) {
    super(injector);
  }

  get control(): AbstractControl | null {
    const ngControlDirective = this.injector.get(NgControl);
    const control = ngControlDirective.control as any | null;
    if (control) {
      const validators: ValidatorFn[] = Array.isArray(control._rawValidators)
        ? control._rawValidators
        : !control._rawValidators
        ? []
        : [control._rawValidators];
      const selfValidators: (ValidatorFn | null)[] = [];

      if (validators.indexOf(DateRangeValidators.dateRangeMinMaxCheck) < 0) {
        selfValidators.push(DateRangeValidators.dateRangeMinMaxCheck);
      }
      if (validators.indexOf(DateRangeValidators.dateRangeValidFormat) < 0) {
        selfValidators.push(DateRangeValidators.dateRangeValidFormat);
      }
      if (
        this.isDateRangeRequired &&
        validators.indexOf(DateRangeValidators.dateRangeRequired) < 0
      ) {
        selfValidators.push(DateRangeValidators.dateRangeRequired);
      }

      if (selfValidators.length > 0) {
        control.setValidators([...validators, ...selfValidators]);
        if (this.rangeSetting) {
          control.addValidators(
            ValidatorsPlus.dateRangeValidateWithSetting(this.rangeSetting)
          );
        }
        if (this.greaterThenSysDateCheck) {
          control.addValidators(ValidatorsPlus.dateRangeGreaterThanSysDate);
        }

        if (this.lessThenSysDateCheck) {
          control.addValidators(ValidatorsPlus.dateRangeLessThanSysDate);
        }
        control.updateValueAndValidity();
      }

      this._control = control;
      if (this.submitTouched) {
        this.onModelTouched();
      }
      if (this.forceUpdateValueAndValidity) {
        control.updateValueAndValidity();
      }

      this.getControl.emit(control);
    }
    return this._control;
  }

  get showStartInvalid(): boolean {
    const errors = Object.keys(this.control?.errors ?? {});
    return errors.some((x) =>
      [
        'startInvalidDate',
        'startRequired',
        'rangeInvalid',
        'startDateGreaterThanOrEqual',
        'startDateGreaterThan',
        'exceedMaxDateRange',
        'dateGreaterThenSysdate',
        'startDateGreaterThenSysdate',
        'endDateGreaterThenSysdate',
        'dateLessThenSysdate',
      ].includes(x)
    );
  }

  get showEndInvalid(): boolean {
    const errors = Object.keys(this.control?.errors ?? {});

    return errors.some((x) =>
      [
        'endInvalidDate',
        'endRequired',
        'rangeInvalid',
        'endDateGreaterThanOrEqual',
        'endDateGreaterThan',
        'exceedMaxDateRange',
        'dateGreaterThenSysdate',
        'startDateGreaterThenSysdate',
        'endDateGreaterThenSysdate',
        'dateLessThenSysdate',
      ].includes(x)
    );
  }

  //#region Angular lifecycle hooks>
  ngOnChanges(changes: SimpleChanges): void {
    if (
      isNullOrEmpty(changes['model']) === false &&
      isNullOrEmpty(changes['model'].currentValue)
    ) {
      this.model = {};
    }
    if (
      isNullOrEmpty(changes['readonly']) === false &&
      isNullOrEmpty(changes['readonly'].currentValue) === false
    ) {
      if (!changes['readonly'].firstChange) {
        if (!this.readonly) {
          this.waitForUpdate = true;
        }
      }
    }
    if (isNullOrEmpty(changes['start']) === false) {
      this.model.start = this.start;
      // 傳入的 start date 為空時，更新顯示的 start date
      if (isNullOrEmpty(changes['start']?.currentValue)) {
        this.updateCalendarValue(this.model.start, DateRangeTypeEnum.start);
      }
    }
    if (isNullOrEmpty(changes['end']) === false) {
      this.model.end = this.end;
      // 傳入的 end date 為空時，更新顯示的 end date
      if (isNullOrEmpty(changes['end']?.currentValue)) {
        this.updateCalendarValue(this.model.end, DateRangeTypeEnum.end);
      }
    }
    if (
      isNullOrEmpty(changes['start']) === false ||
      isNullOrEmpty(changes['end']) === false
    ) {
      this.writeValue(this.model);
    }
    if (
      isNullOrEmpty(this.startCalendar) === false &&
      isNullOrEmpty(this.endCalendar) === false
    ) {
      this.runtimeSetup();
      // 若 start date 和 end date 有變動時，需變更 model 中 maxDate 和 minDate
      this.startCalendar.maxDate = this.end ?? null;
      this.endCalendar.minDate = this.model.start;
    }
    this.logger.debug(
      'dateRangeValidateWithSetting',
      this.control,
      this.rangeSetting
    );
  }

  ngOnInit(): void {
    const displayName =
      this.fieldName === ''
        ? ''
        : this.translateService.instant(this.fieldName);
    this.translateParams = { field: displayName };

    if (isNullOrEmpty(this.dateFormat)) {
      this.primeDateFormat = this.transformToPrimeFormat(
        this.envConfig.format.date
      );
    } else {
      this.primeDateFormat = this.dateFormat!;
    }

    this.ngDateFormat = this.transformToNgFormat(this.primeDateFormat!);

    this.translateService.onLangChange.subscribe({
      next: (lang: any) => {
        const displayName =
          this.fieldName === ''
            ? ''
            : this.translateService.instant(this.fieldName);
        this.translateParams = { field: displayName };
      },
    });
  }
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'Backspace') {
      // 如果按下的是 backspace 键，则触发 backspacePressed 事件
      this.backspacePressed.emit();
    }
  }

  ngDoCheck(): void {
    const currentModel = this.control?.value ?? {};
    const currentModelJson = JSON.stringify(currentModel);
    if (currentModelJson !== this.previousModelJson) {
      this.logger.debug(
        `date range model changed.`,
        currentModelJson,
        this.previousModelJson
      );
      this.writeValue(currentModel);
      this.previousModelJson = currentModelJson;
    }
  }

  ngAfterViewInit(): void {
    this.templates.forEach((item) => {
      switch (item.getType()) {
        case 'starterrors':
          this.startErrorMessageTemplate = item.template;
          break;
        case 'enderrors':
          this.endErrorMessageTemplate = item.template;
          break;
        default:
          break;
      }
    });

    this.runtimeSetup();

    this.updateCalendarValue(this.model?.start, DateRangeTypeEnum.start);
    this.updateCalendarValue(this.model?.end, DateRangeTypeEnum.end);

    this.cd.detectChanges();
  }

  ngAfterViewChecked(): void {
    if (this.waitForUpdate) {
      if (
        isNullOrEmpty(this.startCalendar) === false &&
        isNullOrEmpty(this.endCalendar) === false
      ) {
        this.runtimeSetup();
        this.updateCalendarValue(this.model?.start, DateRangeTypeEnum.start);
        this.updateCalendarValue(this.model?.end, DateRangeTypeEnum.end);
        this.cd.detectChanges();
      }
    }
    this.waitForUpdate = false;
  }
  //#endregion Angular lifecycle hooks

  //#region ControlValueAccessor 實作
  writeValue(obj: any): void {
    this.model = obj ?? {};
    this.updateCalendarValue(obj?.start, DateRangeTypeEnum.start);
    this.updateCalendarValue(obj?.end, DateRangeTypeEnum.end);
  }

  registerOnChange(fn: any): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onModelTouched = fn;
  }
  //#endregion ControlValueAccessor 實作

  /**
   * 起日變更
   * p-calendar ngModelChange event
   * @param {*} date
   * @memberof DateRangePickerComponent
   */
  onStartModelChange(date: any): void {
    if (DateTime.fromJSDate(date).isValid) {
      if (this.showTime === false) {
        date = DateTime.fromJSDate(date).set(DAY_STRART_CONFIG).toJSDate();
      }
    }
    this.model.start = date;
    this.startChange.emit(this.model.start);
    this.onModelChange(this.model);
  }

  /**
   * 迄日變更
   * p-calendar ngModelChange event
   * @param {*} date
   * @memberof DateRangePickerComponent
   */
  onEndModelChange(date: any): void {
    if (DateTime.fromJSDate(date).isValid) {
      if (this.showTime === false) {
        date = DateTime.fromJSDate(date).set(DAY_END_CONFIG).toJSDate();
      }
    }
    this.model.end = date;
    this.endChange.emit(this.model.end);
    this.onModelChange(this.model);
  }

  /**
   * 起日 Blur
   * p-calendar onBlur event
   * @param {*} event
   * @memberof DateRangePickerComponent
   */
  onStartCalendarBlur(event: any): void {
    this.dateBlur.emit({ target: 'start', originEvent: event });
  }

  /**
   * 起日 Clear
   * p-calendar onBlur event
   * @param {*} event
   * @memberof DateRangePickerComponent
   */
  onStartCalendarClear(event: any): void {
    this.dateClear.emit({ target: 'start', originEvent: event });
  }
  /**
   * 迄日 Blur
   * p-calendar onBlur event
   * @param {*} event
   * @memberof DateRangePickerComponent
   */
  onEndCalendarBlur(event: any): void {
    this.dateBlur.emit({ target: 'end', originEvent: event });
  }

  /**
   * 迄日 Blur
   * p-calendar onBlur event
   * @param {*} event
   * @memberof DateRangePickerComponent
   */
  onEndCalendarClear(event: any): void {
    this.dateClear.emit({ target: 'end', originEvent: event });
  }

  /**
   * 選取起日
   * p-calendar onSelect event
   * @param {Date} value
   * @memberof DateRangePickerComponent
   */
  onStartCalendarSelect(value: Date): void {
    this.onStartModelChange(value);
    this.dateSelect.emit({ target: 'start', value });
  }

  /**
   * 選取迄日
   * p-calendar onSelect event
   * @param {Date} value
   * @memberof DateRangePickerComponent
   */
  onEndCalendarSelect(value: Date): void {
    this.onEndModelChange(value);
    this.dateSelect.emit({ target: 'end', value });
  }

  /**
   * 起日 Focus
   * p-calendar onFocus event
   * @param {*} event
   * @memberof DateRangePickerComponent
   */
  onStartCalendarFocus(event: any): void {
    this.onModelTouched();
    this.dateFocus.emit({ target: 'start', originEvent: event });
  }

  /**
   * 迄日 Focus
   * p-calendar onFocus event
   * @param {*} event
   * @memberof DateRangePickerComponent
   */
  onEndCalendarFocus(event: any): void {
    this.onModelTouched();
    this.dateFocus.emit({ target: 'end', originEvent: event });
  }

  /**
   * 起日輸入
   * p-calendar onInput event
   * @param {*} event
   * @memberof DateRangePickerComponent
   */
  onStartCalendarInput(event: any): void {
    this.start = this.startCalendar.model;
    this.onStartModelChange(this.startCalendar.model);
    this.dateInput.emit({ target: 'start', originEvent: event });
  }

  /**
   * 迄日輸入
   * p-calendar onInput event
   * @param {*} event
   * @memberof DateRangePickerComponent
   */
  onEndCalendarInput(event: any): void {
    this.end = this.endCalendar.model;
    this.onEndModelChange(this.endCalendar.model);
    this.dateInput.emit({ target: 'end', originEvent: event });
  }

  /**
   * 取得格式化後的日期區間字串
   *
   * @return {*}  {(string | null)}
   * @memberof DateRangePickerComponent
   */
  getFormattedDataRangeStr(): string | null {
    if (!this.model || (!this.model.start && !this.model.end)) {
      return null;
    }
    const ngFormat = this.showTime
      ? `${this.ngDateFormat} HH:mm`
      : this.ngDateFormat;
    const startStr = !this.model.start
      ? '-'
      : formatDate(this.model.start, ngFormat, this.locale);
    const endStr = !this.model.end
      ? '-'
      : formatDate(this.model.end, ngFormat, this.locale);
    const formattedStr = `${startStr} ~ ${endStr}`;

    return formattedStr;
  }

  /**
   * 設置區間設定
   *
   * @private
   * @param {*} [value]
   * @param {DateRangeTypeEnum} [rangeType]
   * @memberof DateRangePickerComponent
   */
  private runtimeSetup(value?: any, rangeType?: DateRangeTypeEnum): void {
    const maxDate = this.maxDate ?? new Date(8640000000000000);
    const minDate = this.minDate ?? new Date(-8640000000000000);
    if (
      isNullOrEmpty(this.startCalendar) === false &&
      isNullOrEmpty(this.endCalendar) === false
    ) {
      this.startCalendar.maxDate = !this.model.end
        ? maxDate
        : this.model.end < maxDate
        ? this.model.end
        : maxDate;
      this.startCalendar.minDate = this.minDate!;
      this.endCalendar.maxDate = this.maxDate!;
      this.endCalendar.minDate = !this.model.start
        ? minDate
        : this.model.start > minDate
        ? this.model.start
        : minDate;

      switch (rangeType) {
        case DateRangeTypeEnum.start:
          this.startCalendar.runtimeSetup(value);
          break;
        case DateRangeTypeEnum.end:
          this.endCalendar.runtimeSetup(value);
          break;
        default:
          break;
      }
    }
  }

  /**
   * 更新 date-picker 值
   *
   * @private
   * @param {*} value
   * @param {DateRangeTypeEnum} rangeType
   * @memberof DateRangePickerComponent
   */
  private updateCalendarValue(value: any, rangeType: DateRangeTypeEnum): void {
    if (
      isNullOrEmpty(this.startCalendar) === false &&
      isNullOrEmpty(this.endCalendar) === false
    ) {
      switch (rangeType) {
        case DateRangeTypeEnum.start:
          this.start = value;
          this.startCalendar.writeValue(value);
          break;
        case DateRangeTypeEnum.end:
          this.end = value;
          this.endCalendar.writeValue(value);
          break;
        default:
          break;
      }
      this.runtimeSetup(value, rangeType);
    }
  }

  /**
   * 轉換 Angular 日期格式至 Primeng 日期格式
   *
   * @private
   * @param {string} format Angular 日期格式
   * @return {*}  {string}
   * @memberof DateRangePickerComponent
   */
  private transformToPrimeFormat(format: string): string {
    let primeFormat = '';
    let regex = /yyyy/;
    primeFormat = format.replace(regex, 'yy');
    regex = /MM/;
    primeFormat = primeFormat.replace(regex, 'mm');
    return primeFormat;
  }

  /**
   * 轉換 Primeng 日期格式至 Angular 日期格式
   *
   * @private
   * @param {string} format Primeng 日期格式
   * @return {*}  {string}
   * @memberof DateRangePickerComponent
   */
  private transformToNgFormat(format: string): string {
    let ngFormat = '';
    let regex = /yy/;
    ngFormat = format.replace(regex, 'yyyy');
    regex = /mm/;
    ngFormat = ngFormat.replace(regex, 'MM');
    return ngFormat;
  }
}

/**
 * 日期區間相關檢核
 *
 * @export
 * @class DateRangeValidators
 */
export class DateRangeValidators {
  /**
   * 日期區間大於(等於)檢核
   *
   * @static
   * @param {Date} minDate
   * @param {boolean} [orEqual=false]
   * @return {*}  {(ValidatorFn | null)}
   * @memberof DateRangeValidators
   */
  static dateRangeGreaterThan(
    minDate: Date,
    orEqual = false
  ): ValidatorFn | null {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (isNullOrEmpty(ctrl.value)) {
        return null;
      }
      const start = isNullOrEmpty(ctrl.value?.start)
        ? null
        : DateTime.fromJSDate(ctrl.value.start).set({
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
          });
      const end = isNullOrEmpty(ctrl.value?.end)
        ? null
        : DateTime.fromJSDate(ctrl.value.end).set({
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
          });
      const targetDate = DateTime.fromJSDate(minDate);
      const errorObj: any = {};
      if (isNullOrEmpty(start) === false) {
        if (orEqual) {
          if (start! < targetDate) {
            errorObj.startDateGreaterThanOrEqual = { minDate };
          }
        } else {
          if (start! <= targetDate) {
            errorObj.startDateGreaterThan = { minDate };
          }
        }
      }
      if (isNullOrEmpty(end) === false) {
        if (orEqual) {
          if (end! < targetDate) {
            errorObj.endDateGreaterThanOrEqual = { minDate };
          }
        } else {
          if (end! <= targetDate) {
            errorObj.endDateGreaterThan = { minDate };
          }
        }
      }
      return Object.keys(errorObj).length > 0 ? errorObj : null;
    };
  }

  /**
   * 日期區間起迄日檢核
   *
   * @static
   * @param {AbstractControl} ctrl
   * @return {*}  {(ValidationErrors | null)}
   * @memberof DateRangeValidators
   */
  static dateRangeMinMaxCheck(ctrl: AbstractControl): ValidationErrors | null {
    const val = ctrl.value as { start?: Date; end?: Date };
    if (
      isNullOrEmpty(val?.start) === false &&
      isNullOrEmpty(val?.end) === false
    ) {
      if (val.start! > val.end!) {
        return { rangeInvalid: true };
      }
    }
    return null;
  }

  /**
   * 日期區間起迄日檢核(Submit)
   *
   * @static
   * @param {AbstractControl} ctrl
   * @return {*}  {(ValidationErrors | null)}
   * @memberof DateRangeValidators
   */
  static dateRangeMinMaxCheckOnSubmit(
    ctrl: AbstractControl
  ): ValidationErrors | null {
    const formEx = getFormGroupEx(ctrl);
    if (!formEx?.submitted) {
      return null;
    }
    const val = ctrl.value as { start?: Date; end?: Date };
    if (
      isNullOrEmpty(val?.start) === false &&
      isNullOrEmpty(val?.end) === false
    ) {
      if (val.start! > val.end!) {
        return { rangeInvalid: true };
      }
    }
    return null;
  }

  /**
   * 日期區間必填檢核
   *
   * @static
   * @param {AbstractControl} ctrl
   * @return {*}  {(ValidationErrors | null)}
   * @memberof DateRangeValidators
   */
  static dateRangeRequired(ctrl: AbstractControl): ValidationErrors | null {
    const val = ctrl.value as { start?: Date; end?: Date };
    const errObj = {} as any;
    if (isNullOrEmpty(val?.start) && isNullOrEmpty(val?.end) === false) {
      // 起日為空
      errObj.startRequired = true;
    } else if (isNullOrEmpty(val?.start) === false && isNullOrEmpty(val?.end)) {
      // 迄日為空
      errObj.endRequired = true;
    } else {
      return null;
    }
    return Object.keys(errObj).length === 0 ? null : errObj;
  }

  /**
   * 日期區間必填檢核(Submit)
   *
   * @static
   * @param {AbstractControl} control
   * @return {*}  {(ValidationErrors | null)}
   * @memberof DateRangeValidators
   */
  static dateRangeRequiredOnSubmit(
    control: AbstractControl
  ): ValidationErrors | null {
    if (isNullOrEmpty(control)) return null;
    const formEx = getFormGroupEx(control);
    return formEx?.submitted
      ? DateRangeValidators.dateRangeRequired(control)
      : null;
  }

  /**
   * 日期區間格式檢核
   *
   * @static
   * @param {AbstractControl} ctrl
   * @return {*}  {(ValidationErrors | null)}
   * @memberof DateRangeValidators
   */
  static dateRangeValidFormat(ctrl: AbstractControl): ValidationErrors | null {
    function checkIsValidDate(date?: Date): boolean {
      if (isNullOrEmpty(date)) return true;
      const datetime = DateTime.fromJSDate(date!);
      return datetime.isValid;
    }

    const val = ctrl.value as { start?: Date; end?: Date };
    const errObj = {} as any;
    if (checkIsValidDate(val?.start) === false) {
      errObj.startInvalidDate = true;
    }
    if (checkIsValidDate(val?.end) === false) {
      errObj.endInvalidDate = true;
    }

    return Object.keys(errObj).length === 0 ? null : errObj;
  }
}
